// Holt office navigation. Recast/Detour finds the route in a navmesh; the caller provides its initialized
// implementation so the browser and workerd can load the same engine with their different WASM rules.
// The authoritative Deck collider still decides whether every returned segment is safe for the real feet box.
import { BODY, SEA_MARGIN } from './grid.js';
import { boxFits, sweepClear } from './geometry.js';
export { boxFits, sweepClear } from './geometry.js';
export const CLEAR = 0.10;
const VARIANTS = [[0.1, 0.1], [0.05, 0.1]];
const MAX_TILES = 100_000, MAX_CACHED = 1, QUERY_LIMIT = 32768, MAX_NEAREST_CANDIDATES = 100_000;
const MID_X = (BODY.l + BODY.r) / 2, MID_Y = (BODY.t + BODY.b) / 2;
const HALF_X = (BODY.r - BODY.l) / 2, HALF_Y = (BODY.b - BODY.t) / 2;

/** A failed engine is different from a reachable-space search finding no path. */
export class NavigationUnavailable extends Error {
  constructor() { super('Navigation is not available'); this.name = 'NavigationUnavailable'; }
}
export class NavigationCancelled extends Error {
  constructor() { super('Navigation was cancelled'); this.name = 'NavigationCancelled'; }
}
export class NavigationInvalidated extends Error {
  constructor() { super('Navigation map changed'); this.name = 'NavigationInvalidated'; }
}
const pause = () => new Promise((resolve) => setTimeout(resolve, 0));
const check = (signal) => { if (signal?.aborted) throw new NavigationCancelled(); };
const known = (e) => e instanceof NavigationCancelled || e instanceof NavigationInvalidated || e instanceof NavigationUnavailable;
let engine = null;
export function configureNavigation(impl) {
  if (!impl || typeof impl.NavMeshQuery !== 'function' || typeof impl.createTiledBuilder !== 'function'
      || typeof impl.destroy !== 'function' || !Number.isInteger(impl.fatalStatusMask)) throw new NavigationUnavailable();
  if (engine) clearNavigationCache();
  engine = impl;
}
const cache = new Map(), jobs = new Map(), liveEntries = new Set();
let buildTail = Promise.resolve(), nearestTail = Promise.resolve();
function dispose(entry) {
  if (!entry) return;
  // QueryFilter is allocated separately by this library version and is not released by query.destroy().
  liveEntries.delete(entry);
  try { entry.query.destroy(); } finally {
    try { entry.destroy(entry.query.defaultFilter.raw); } finally { entry.nav.destroy(); }
  }
}
/** Release native resources, e.g. when a host unloads a room or a browser stops using the planner. */
export function clearNavigationCache() {
  for (const job of jobs.values()) job.controller.abort(); jobs.clear();
  const entries = [...cache.values()]; cache.clear();
  let failed = false;
  for (const entry of entries) { entry.retired = true; if (!entry.refs) try { dispose(entry); } catch { failed = true; } }
  if (failed) throw new NavigationUnavailable();
}

/** Exact rectangle expansion of all blocked tiles. Rectangles cover the remaining body-center space. */
async function floorMesh(rows, clearance, signal) {
  const hx = HALF_X + clearance, hy = HALF_Y + clearance;
  const h = rows.length, w = rows[0].length, left = hx, right = w - hx, low = hy, high = h - hy;
  if (right <= left || high <= low) return { positions: [], indices: [] };
  const cuts = new Set([low, high]);
  for (let y = 0; y < h; y++) for (const z of [y - hy, y + 1 + hy]) if (z > low && z < high) cuts.add(z);
  const ys = [...cuts].sort((a, b) => a - b), positions = [], indices = [];
  const rect = (x0, y0, x1, y1) => {
    if (x1 - x0 < 1e-9 || y1 - y0 < 1e-9) return;
    const i = positions.length / 3;
    positions.push(x0, 0, y0, x1, 0, y0, x1, 0, y1, x0, 0, y1);
    indices.push(i, i + 2, i + 1, i, i + 3, i + 2);
  };
  for (let band = 1; band < ys.length; band++) {
    if (band % 8 === 0) { await pause(); check(signal); }
    const y0 = ys[band - 1], y1 = ys[band], mid = (y0 + y1) / 2, blocked = new Uint8Array(w);
    for (let y = Math.max(0, Math.floor(mid - hy) - 1); y <= Math.min(h - 1, Math.floor(mid + hy)); y++) {
      if (!(mid > y - hy && mid < y + 1 + hy)) continue;
      for (let x = 0; x < w; x++) if (rows[y][x] === '1') blocked[x] = 1;
    }
    let x = 0, cursor = left;
    while (x < w) {
      if (!blocked[x]) { x++; continue; }
      const from = x; while (x < w && blocked[x]) x++;
      const lo = Math.max(left, from - hx), hi = Math.min(right, x + hx);
      if (lo > cursor) rect(cursor, y0, lo, y1);
      cursor = Math.max(cursor, hi);
    }
    if (cursor < right) rect(cursor, y0, right, y1);
  }
  return { positions, indices };
}
function snapshot(solid, map, boat, clearance, cell) {
  const margin = boat ? SEA_MARGIN : 0;
  const x0 = (map.x0 ?? 0) - margin, y0 = (map.y0 ?? 0) - margin;
  const width = map.width + margin * 2, height = map.height + margin * 2;
  if (![x0, y0, width, height].every(Number.isSafeInteger) || width <= 0 || height <= 0 || width * height > MAX_TILES)
    throw new NavigationUnavailable();
  const rows = [];
  for (let y = 0; y < height; y++) {
    let row = ''; for (let x = 0; x < width; x++) row += solid(x0 + x, y0 + y, boat) ? '1' : '0';
    rows.push(row);
  }
  return { x0, y0, width, height, rows, key: `${width},${height},${x0},${y0},${clearance},${cell}|${rows.join('')}` };
}
function assertCurrent(snap, solid, map, boat, clearance, cell) {
  if (snapshot(solid, map, boat, clearance, cell).key !== snap.key) throw new NavigationInvalidated();
}
function release(entry) {
  if (!entry) return; entry.refs--;
  if (entry.refs === 0 && entry.retired) dispose(entry);
}
function putCache(entry) {
  while (cache.size >= MAX_CACHED) {
    const key = [...cache.keys()].find((k) => cache.get(k).refs === 0);
    if (key === undefined) { entry.retired = true; return; }
    const old = cache.get(key); cache.delete(key); old.retired = true; dispose(old);
  }
  cache.set(entry.key, entry);
}
function subscribe(job, signal) {
  check(signal);
  return new Promise((resolve, reject) => {
    const sub = { resolve, reject, signal, onAbort: null };
    sub.onAbort = () => {
      if (!job.subs.delete(sub)) return;
      signal.removeEventListener('abort', sub.onAbort); reject(new NavigationCancelled());
      if (!job.subs.size && !job.done) { job.controller.abort(); if (jobs.get(job.key) === job) jobs.delete(job.key); }
    };
    job.subs.add(sub); signal?.addEventListener('abort', sub.onAbort, { once: true });
    if (signal?.aborted) sub.onAbort();
  });
}
function deliver(job, entry, error) {
  job.done = true; if (jobs.get(job.key) === job) jobs.delete(job.key);
  const subs = [...job.subs]; job.subs.clear();
  if (entry) entry.refs = subs.length;
  try { if (entry && subs.length) putCache(entry); else if (entry) dispose(entry); }
  catch (e) { if (entry) try { dispose(entry); } catch {} entry = null; error = new NavigationUnavailable(); }
  for (const sub of subs) {
    sub.signal?.removeEventListener('abort', sub.onAbort);
    if (error) sub.reject(error); else sub.resolve(entry);
  }
}
async function buildGrid(job) {
  const { snap, solid, map, boat, clearance, cell } = job, signal = job.controller.signal;
  // A large mesh and its build scratch can approach the isolate's memory limit. Wait for any in-use mesh,
  // evict unpinned meshes before allocating the next one, and never hold two meshes across a yield.
  for (;;) {
    check(signal);
    for (const entry of [...liveEntries]) if (!entry.refs) {
      if (cache.get(entry.key) === entry) cache.delete(entry.key);
      entry.retired = true; dispose(entry);
    }
    if (!liveEntries.size) break;
    await pause();
  }
  const { positions, indices } = await floorMesh(snap.rows, clearance, signal); check(signal);
  if (!indices.length) { assertCurrent(snap, solid, map, boat, clearance, cell); check(signal); return null; }
  let builder, nav, query;
  try {
    builder = engine.createTiledBuilder(positions, indices, { tileSize: 128, cs: cell, ch: 0.2, walkableHeight: 2,
      walkableClimb: 1, walkableRadius: 0, minRegionArea: 0, mergeRegionArea: 0, maxSimplificationError: 0.15, maxEdgeLen: 40 });
    while (!builder.step()) { await pause(); check(signal); }
    check(signal); nav = builder.finish(); builder = null;
    assertCurrent(snap, solid, map, boat, clearance, cell); check(signal);
    query = new engine.NavMeshQuery(nav, { maxNodes: QUERY_LIMIT });
    const entry = { nav, query, destroy: engine.destroy, ...snap, refs: 0, retired: false };
    liveEntries.add(entry); return entry;
  } catch (error) {
    if (builder) try { builder.cancel(); } catch {}
    if (query) try { query.destroy(); } catch {}
    if (nav) try { nav.destroy(); } catch {}
    throw known(error) ? error : new NavigationUnavailable();
  }
}
async function gridFor(solid, map, boat, clearance, cell, signal) {
  check(signal); if (!engine) throw new NavigationUnavailable();
  const snap = snapshot(solid, map, boat, clearance, cell), found = cache.get(snap.key);
  if (found) { cache.delete(snap.key); cache.set(snap.key, found); found.refs++; return { entry: found, snap }; }
  let job = jobs.get(snap.key);
  if (!job) {
    if (jobs.size >= 8) throw new NavigationUnavailable();
    job = { key: snap.key, snap, solid, map, boat, clearance, cell, controller: new AbortController(), subs: new Set(), done: false };
    jobs.set(snap.key, job);
    const queued = buildTail.then(async () => { await pause(); return buildGrid(job); });
    buildTail = queued.then(() => {}, () => {});
    queued.then((entry) => deliver(job, entry, null), (error) => deliver(job, null, known(error) ? error : new NavigationUnavailable()));
  }
  const entry = await subscribe(job, signal);
  if (signal?.aborted) { release(entry); throw new NavigationCancelled(); }
  return { entry, snap };
}
const world = (g, x, y) => ({ x: x + MID_X - g.x0, y: 0, z: y + MID_Y - g.y0 });
const fromWorld = (g, p) => [p.x - MID_X + g.x0, p.z - MID_Y + g.y0];
function statusOK(result) {
  if (!result.success || (result.status & engine.fatalStatusMask)) throw new NavigationUnavailable();
}
function nearest(query, point, extent) {
  const n = query.findNearestPoly(point, { halfExtents: { x: extent, y: 1, z: extent } });
  statusOK(n);
  return n.nearestRef ? n : null;
}
// The nearest polygon can lie across an obstacle, especially at a tile seam. Search nearby only when that
// projection cannot be safely connected; every candidate must be connected by a real body sweep.
function anchor(g, solid, boat, x, y, extent) {
  const projected = nearest(g.query, world(g, x, y), extent);
  if (projected && sweepClear(solid, x, y, ...fromWorld(g, projected.nearestPoint), boat, 0)) return projected;
  for (const radius of [0.15, 0.3, 0.5, 0.8, 1.2, 1.8, 2.5]) {
    for (let i = 0; i < 16; i++) {
      const angle = i * Math.PI / 8, cx = x + radius * Math.cos(angle), cy = y + radius * Math.sin(angle);
      if (!boxFits(solid, cx, cy, boat) || !sweepClear(solid, x, y, cx, cy, boat, 0)) continue;
      const n = nearest(g.query, world(g, cx, cy), 0.3);
      if (n && sweepClear(solid, x, y, ...fromWorld(g, n.nearestPoint), boat, 0)) return n;
    }
  }
  return null;
}
/** Verify endpoints, graph completeness and every swept segment; a projected/partial path is never a full path. */
function route(g, solid, boat, sx, sy, gx, gy, start, finish) {
  const q = g.query, a = start ?? anchor(g, solid, boat, sx, sy, 3);
  const b = finish ?? anchor(g, solid, boat, gx, gy, 3);
  if (!a || !b) return null;
  const aa = a.nearestPoint, bb = b.nearestPoint, axy = fromWorld(g, aa), bxy = fromWorld(g, bb);
  if (!sweepClear(solid, sx, sy, ...axy, boat, 0)) return null;
  if (!sweepClear(solid, ...bxy, gx, gy, boat, 0)) return null;
  let corridor, straight;
  try {
    corridor = q.findPath(a.nearestRef, b.nearestRef, aa, bb, { maxPathPolys: QUERY_LIMIT });
    statusOK(corridor);
    if (!corridor.polys.size) return null;
    const lastRef = corridor.polys.get(corridor.polys.size - 1);
    if (lastRef !== b.nearestRef) return null;
    straight = q.findStraightPath(aa, bb, corridor.polys, { maxStraightPathPoints: QUERY_LIMIT });
    statusOK(straight);
    if (!straight.straightPathCount) return null;
    const points = [[sx, sy]];
    for (let i = 0; i < straight.straightPathCount; i++) {
      const a = straight.straightPath; points.push(fromWorld(g, { x: a.get(i * 3), z: a.get(i * 3 + 2) }));
    }
    points.push([gx, gy]);
    for (let i = 1; i < points.length; i++) if (!sweepClear(solid, ...points[i - 1], ...points[i], boat, 0)) return null;
    const clean = [];
    for (let i = 1; i < points.length; i++) if (Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]) > 1e-7) clean.push(points[i]);
    return { points: clean };
  } finally {
    // The convenience computePath method in 0.44.0 leaks these native arrays on several early failures.
    if (straight) { straight.straightPath.destroy(); straight.straightPathFlags.destroy(); straight.straightPathRefs.destroy(); }
    if (corridor) corridor.polys.destroy();
  }
}
const distance = (points, sx, sy) => { let x = sx, y = sy, sum = 0; for (const p of points) { sum += Math.hypot(p[0] - x, p[1] - y); [x, y] = p; } return sum; };

/** A path for the real body to an exact position or a tile region. May yield and accepts an AbortSignal;
 *  a cancelled, invalidated or unavailable query throws its distinct error instead of reporting no path. */
export async function findPath({ solid, map, boat, signal }, sx, sy, goal) {
  check(signal);
  if (!engine) throw new NavigationUnavailable();
  if (![sx, sy].every(Number.isFinite) || !goal || !boxFits(solid, sx, sy, boat)) return null;
  const exact = goal.x !== undefined;
  if (exact && ![goal.x, goal.y].every(Number.isFinite)) return null;
  let goals;
  if (exact) goals = [[goal.x, goal.y]];
  else {
    const r = goal.r ?? 1;
    if (!Number.isSafeInteger(goal.tx) || !Number.isSafeInteger(goal.ty) || !Number.isSafeInteger(r) || r < 0 || r > 8) return null;
    if (Math.abs(Math.floor(sx + 0.5) - goal.tx) <= r && Math.abs(Math.floor(sy + 0.8) - goal.ty) <= r) return [];
    goals = [];
    for (let y = goal.ty - r; y <= goal.ty + r; y++) for (let x = goal.tx - r; x <= goal.tx + r; x++) goals.push([x, y - 0.3]);
  }
  goals = goals.filter(([x, y]) => boxFits(solid, x, y, boat));
  if (!goals.length) return null;
  // Straight, collision-checked travel does not need a graph query or a mesh rebuild.
  let best = null, bestDistance = Infinity;
  for (const [x, y] of goals) if (sweepClear(solid, sx, sy, x, y, boat, 0)) {
    const d = Math.hypot(x - sx, y - sy); if (d < bestDistance) { best = [[x, y]]; bestDistance = d; }
  }
  if (best) return best;
  try {
    // Two conservative rasterizations cope with Recast tile seams and pockets near an endpoint. They are both
    // configuration-space meshes; no grid planner or unchecked route is used as a fallback.
    for (const [clearance, cell] of VARIANTS) {
      const { entry: g, snap } = await gridFor(solid, map, !!boat, clearance, cell, signal); if (!g) continue;
      try {
        check(signal); assertCurrent(snap, solid, map, !!boat, clearance, cell);
        let checked = 0;
        for (const [x, y] of goals) {
          if (checked && checked % 2 === 0) { await pause(); check(signal); }
          checked++;
          const p = route(g, solid, boat, sx, sy, x, y); if (!p) continue;
          const d = distance(p.points, sx, sy); if (d < bestDistance) { best = p.points; bestDistance = d; }
        }
        check(signal); assertCurrent(snap, solid, map, !!boat, clearance, cell);
      } finally { release(g); }
      if (best) break;
    }
  } catch (error) { throw known(error) ? error : new NavigationUnavailable(); }
  return best;
}
/** The nearest reachable tile, by distance from the requested tile (like a click on an unreachable place). */
async function nearestTileInner({ solid, map, boat, signal }, sx, sy, tx, ty) {
  if (!engine) throw new NavigationUnavailable();
  if (![sx, sy, tx, ty].every(Number.isFinite) || !boxFits(solid, sx, sy, boat)) return null;
  try {
    let best = null, bestDistance = Infinity, geometricBest = Infinity;
    for (const [clearance, cell] of VARIANTS) {
      const { entry: g, snap } = await gridFor(solid, map, !!boat, clearance, cell, signal); if (!g) continue;
      try {
      check(signal); assertCurrent(snap, solid, map, !!boat, clearance, cell);
      const start = anchor(g, solid, boat, sx, sy, 3); if (!start) continue;
      const reachable = g.query.findPolysAroundCircle(start.nearestRef, start.nearestPoint,
        Math.hypot(g.width, g.height) + 10, { maxPolys: QUERY_LIMIT });
      statusOK(reachable);
      if (reachable.resultCount >= QUERY_LIMIT) throw new NavigationUnavailable();
      const refs = new Set(reachable.resultRefs.slice(0, reachable.resultCount));
      const xmin = g.x0, xmax = xmin + g.width - 1, ymin = g.y0, ymax = ymin + g.height - 1;
      const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
      const cx = clamp(Math.round(tx), xmin, xmax), cy = clamp(Math.round(ty), ymin, ymax);
      const d2 = (x, y) => (x - tx) ** 2 + (y - ty) ** 2;
      const maxRadius = Math.max(cx - xmin, xmax - cx, cy - ymin, ymax - cy);
      let checked = 0, visited = 0;
      const visit = async (x, y) => {
        if (x < xmin || x > xmax || y < ymin || y > ymax) return;
        if (++visited % 16 === 0) { await pause(); check(signal); }
        if (solid(x, y, boat) || !boxFits(solid, x, y - 0.3, boat)) return;
        const d = d2(x, y); geometricBest = Math.min(geometricBest, d);
        if (d >= bestDistance) return;
        if (++checked > MAX_NEAREST_CANDIDATES) throw new NavigationUnavailable();
        const n = nearest(g.query, world(g, x, y - 0.3), 0.3);
        if (!n || !refs.has(n.nearestRef) || !sweepClear(solid, x, y - 0.3, ...fromWorld(g, n.nearestPoint), boat, 0)) return;
        if (route(g, solid, boat, sx, sy, x, y - 0.3, start, n)) { best = [x, y]; bestDistance = d; }
      };
      const lowerBound = (r) => {
        // Tiles outside the current square lie in one of these four strips. Find a lower bound on their distance.
        let d = Infinity;
        const rect = (x0, x1, y0, y1) => { if (x0 <= x1 && y0 <= y1) d = Math.min(d, d2(clamp(tx, x0, x1), clamp(ty, y0, y1))); };
        rect(xmin, Math.min(xmax, cx - r - 1), ymin, ymax);
        rect(Math.max(xmin, cx + r + 1), xmax, ymin, ymax);
        rect(xmin, xmax, ymin, Math.min(ymax, cy - r - 1));
        rect(xmin, xmax, Math.max(ymin, cy + r + 1), ymax);
        return d;
      };
      for (let r = 0; r <= maxRadius; r++) {
        if (r === 0) await visit(cx, cy);
        else {
          for (let x = cx - r; x <= cx + r; x++) { await visit(x, cy - r); await visit(x, cy + r); }
          for (let y = cy - r + 1; y < cy + r; y++) { await visit(cx - r, y); await visit(cx + r, y); }
        }
        if (best && bestDistance <= lowerBound(r)) break;
      }
      check(signal); assertCurrent(snap, solid, map, !!boat, clearance, cell);
      } finally { release(g); }
      if (best && bestDistance === geometricBest) break;
    }
    return best;
  } catch (error) { throw known(error) ? error : new NavigationUnavailable(); }
}
/** Serialize long nearest searches: only one can pin a native mesh across yields in an isolate. */
export function nearestTile(options, sx, sy, tx, ty) {
  const result = nearestTail.then(() => { check(options.signal); return nearestTileInner(options, sx, sy, tx, ty); });
  nearestTail = result.then(() => {}, () => {});
  const signal = options.signal; if (!signal) return result;
  return new Promise((resolve, reject) => {
    const abort = () => { signal.removeEventListener('abort', abort); reject(new NavigationCancelled()); };
    signal.addEventListener('abort', abort, { once: true }); if (signal.aborted) abort();
    result.then((value) => { signal.removeEventListener('abort', abort); resolve(value); }, (error) => { signal.removeEventListener('abort', abort); reject(error); });
  });
}
